<template>
  <div class="page-container">
    <div class="page-header">
      <h2>任务派遣单</h2>
      <div class="header-actions">
        <el-input
          v-model="filterKeyword"
          clearable
          size="small"
          placeholder="搜索项目编号 / 名称 / 采样人"
          style="width:240px"
          @clear="onFilterClear"
        />
        <el-button type="primary" @click="createTask">新建任务</el-button>
      </div>
    </div>
    <el-alert
      v-if="routeXmbh"
      type="info"
      :closable="true"
      show-icon
      class="filter-banner"
      @close="clearRouteFilter"
    >
      当前显示项目 <strong>{{ routeXmbh }}</strong> 的派遣单（共 {{ filteredList.length }} 条）
      <el-button v-if="!filteredList.length" type="text" size="mini" @click="clearRouteFilter">查看全部</el-button>
    </el-alert>
    <el-table :data="filteredList" v-loading="loading" stripe border size="small">
      <el-table-column prop="xmbh" label="项目编号" width="200"></el-table-column>
      <el-table-column prop="xmmc" label="项目名称" min-width="160"></el-table-column>
      <el-table-column label="委托单位" width="150">
        <template slot-scope="{row}">{{ sj(row).wtdw || '—' }}</template>
      </el-table-column>
      <el-table-column label="期次/计划日" width="150">
        <template slot-scope="{row}">{{ dispatchPeriodLabel(row) }}</template>
      </el-table-column>
      <el-table-column label="任务编号" width="140">
        <template slot-scope="{row}">{{ sj(row).rwbh || row.rwbh || '—' }}</template>
      </el-table-column>
      <el-table-column label="采样人员" width="100">
        <template slot-scope="{row}">{{ sj(row).cyry || '—' }}</template>
      </el-table-column>
      <el-table-column label="项目阶段" width="110">
        <template slot-scope="{row}">
          <el-tag v-if="phaseForRow(row)" size="small" :type="phaseTag(phaseForRow(row).xmzt)">
            {{ phaseForRow(row).xmzt_label }}
          </el-tag>
          <span v-else style="color:#c0c4cc">—</span>
        </template>
      </el-table-column>
      <el-table-column label="状态" width="90">
        <template slot-scope="{row}">
          <el-tag :type="ztTag(row.bdzt)" size="small">{{ ztLabel(row.bdzt) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column v-if="showCreatorCol" prop="cjr_xm" label="创建人" width="90"></el-table-column>
      <el-table-column label="操作" width="260" fixed="right">
        <template slot-scope="{row}">
          <el-button v-if="sj(row).xmbh || row.xmbh" type="text" size="small" @click="$router.push({ path: '/task-track', query: { xmbh: row.xmbh || sj(row).xmbh } })">任务监控</el-button>
          <el-button v-if="canEdit(row) && !isRoutineDispatch(row)" type="text" size="small" @click="fillForm(row)">填写</el-button>
          <el-button v-if="isRoutineDispatch(row)" type="text" size="small" @click="goRoutinePlan(row)">在安排页编辑</el-button>
          <el-button type="text" size="small" @click="viewTask(row)">查看</el-button>
          <el-button v-if="canEdit(row)" type="text" size="small" @click="del(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog title="选择项目" :visible.sync="showSelectProject" width="500px">
      <el-select v-model="selectedProjectId" filterable placeholder="请选择项目" style="width:100%">
        <el-option v-for="p in projects" :key="p.id" :label="p.xmbh+' - '+p.xmmc" :value="p.id"></el-option>
      </el-select>
      <div slot="footer">
        <el-button @click="showSelectProject=false">取消</el-button>
        <el-button type="primary" :disabled="!selectedProjectId" @click="confirmCreateTask">确定</el-button>
      </div>
    </el-dialog>

    <!-- 任务派遣单：Element UI 表单（填写 / 查看共用） -->
    <el-dialog :title="formDialogTitle" :visible.sync="showFill" width="800px" top="5vh" :close-on-click-modal="false">
      <div v-if="loadingForm" style="text-align:center;padding:60px;color:#909399">加载中...</div>
      <el-form v-else-if="formData" ref="form" label-width="140px" label-position="left" size="small">
        <DispatchFormBase v-model="formData" :readonly="formReadonly" />
        <el-form-item label="备注">
          <el-input type="textarea" v-model="formData.bz" :autosize="{ minRows: 2 }" :disabled="formReadonly" />
        </el-form-item>
        <DispatchFormExtra
          v-model="formData"
          :users="users"
          :readonly="formReadonly"
          :show-cycsts="true"
          :show-handover-time="true"
        />
      </el-form>
      <div v-else style="text-align:center;padding:40px;color:#909399">加载失败</div>
      <div slot="footer">
        <el-button @click="showFill=false">{{ formReadonly ? '关闭' : '取消' }}</el-button>
        <el-button v-if="!formReadonly" type="primary" @click="saveForm">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import api from '@/utils/api'
import axios from 'axios'
import DispatchFormBase from '@/components/DispatchFormBase.vue'
import DispatchFormExtra from '@/components/DispatchFormExtra.vue'
import { getCurrentUser, canEditRecord, canViewAllRecords } from '@/utils/user'

export default {
  components: { DispatchFormBase, DispatchFormExtra },
  data() {
    return {
      list: [], loading: false, mbid: null,
      filterKeyword: '',
      routeXmbh: '',
      phaseMap: {},
      projects: [], showSelectProject: false, selectedProjectId: null, pendingXmid: null,
      users: [],
      showFill: false, editId: null, formData: {}, loadingForm: false,
      formReadonly: false, formDialogTitle: '任务派遣单',
      saveXmid: null, saveXmmc: '', saveBdmc: '', saveMbId: null, saveRow: null,
    }
  },
  computed: {
    showCreatorCol() { return canViewAllRecords() },
    currentUser() { return getCurrentUser() },
    filteredList() {
      const route = String(this.routeXmbh || '').trim()
      const kw = String(this.filterKeyword || '').trim().toLowerCase()
      let rows = this.list
      if (route) {
        rows = rows.filter(row => {
          const xmbh = String(row.xmbh || this.sj(row).xmbh || '')
          return xmbh === route
        })
      }
      if (kw && (!route || kw !== route.toLowerCase())) {
        rows = rows.filter(row => {
          const s = this.sj(row)
          const hay = [row.xmbh, row.xmmc, s.wtdw, s.cyry, s.xmfzr, s.pqdd]
            .map(v => String(v || '').toLowerCase()).join(' ')
          return hay.indexOf(kw) >= 0
        })
      }
      return rows
    }
  },
  watch: {
    '$route.query.xmbh'(val) {
      this.applyRouteFilter(val)
    }
  },
  mounted() {
    this.applyRouteFilter(this.$route.query.xmbh)
    const xmid = this.$route.query.xmid
    if (xmid) this.pendingXmid = xmid
    this.loadUsers()
    this.loadTemplate()
  },
  methods: {
    sj(row) { try { return JSON.parse(row.sj_json || '{}') } catch (e) { return {} } },
    applyRouteFilter(xmbh) {
      const v = String(xmbh || '').trim()
      this.routeXmbh = v
      this.filterKeyword = v
    },
    clearRouteFilter() {
      this.routeXmbh = ''
      this.filterKeyword = ''
      if (this.$route.query.xmbh || this.$route.query.auto) {
        this.$router.replace({ path: '/dispatch-list', query: {} }).catch(() => {})
      }
    },
    maybeAutoOpen() {
      const auto = String(this.$route.query.auto || '').trim()
      if (auto !== 'view' && auto !== 'fill') return
      if (!this.routeXmbh) return
      if (!this.filteredList.length) {
        if (auto === 'fill') {
          this.$message.info('该项目暂无135派遣单，请点击「新建任务」创建')
        } else {
          this.$message.info('该项目暂无135派遣单记录')
        }
        return
      }
      const row = this.filteredList[0]
      if (auto === 'fill') this.fillForm(row)
      else this.viewTask(row)
      const q = { ...this.$route.query }
      delete q.auto
      this.$router.replace({ path: this.$route.path, query: q }).catch(() => {})
    },
    onFilterClear() {
      if (this.routeXmbh) this.clearRouteFilter()
      else this.load()
    },
    dispatchPeriodLabel(row) {
      const s = this.sj(row)
      if (s.bz) return s.bz
      const parts = []
      if (s.plan_period) parts.push('第' + s.plan_period + '期')
      if (s.plan_month) parts.push(s.plan_month + '月')
      if (s.yqwcsj) parts.push(s.yqwcsj)
      return parts.length ? parts.join(' · ') : '—'
    },
    isRoutineDispatch(row) {
      const s = this.sj(row)
      if (s.routine_pick_id) return true
      const xmbh = row.xmbh || s.xmbh
      const p = (this.projects || []).find(x => x.xmbh === xmbh)
      return !!(p && p.flow_model === 'RoutineEntrustFlow')
    },
    goRoutinePlan(row) {
      const xmbh = row.xmbh || this.sj(row).xmbh
      if (!xmbh) return
      this.$router.push('/routine/plan/' + encodeURIComponent(xmbh))
    },
    canEdit(row) { return canEditRecord(row) },
    async loadUsers() {
      try {
        const res = await api.get('yn_yh_list')
        this.users = (res.data || []).filter(u => u.zt == 1)
      } catch (e) { this.users = [] }
    },
    async loadTemplate() {
      const m = await axios.get('/raw-forms/manifest.json')
      const tpl = (m.data || []).find(t => t.mbbh.includes('135'))
      if (tpl) {
        const numMatch = String(tpl.mbbh || '').match(/-(\d+)\s*$/)
        this.mbid = numMatch ? -parseInt(numMatch[1], 10) : -(m.data.indexOf(tpl) + 1)
        await this.load()
      }
      const pr = await api.get('yn_xm_list')
      this.projects = (pr.data || []).filter(p => p.flow_model !== 'RoutineEntrustFlow')
      if (this.pendingXmid) {
        this.selectedProjectId = parseInt(this.pendingXmid)
        await this.confirmCreateTask()
        this.pendingXmid = null
      }
    },
    async load() {
      this.loading = true
      try {
        const params = { mbid: this.mbid }
        if (this.routeXmbh) params.xmbh = this.routeXmbh
        const res = await api.get('yn_bdsl_list', params)
        this.list = res.data || []
        await this.loadPhasesForList()
      this.maybeAutoOpen()
    } catch (e) {
        this.$message.error('加载列表失败')
        this.list = []
      }
      this.loading = false
    },
    async createTask() {
      this.selectedProjectId = null
      this.showSelectProject = true
    },
    async confirmCreateTask() {
      const p = this.projects.find(x => x.id == this.selectedProjectId)
      if (!p) return
      let jcxm = ''
      let xcssfa = '土壤'
      let xcssfaNum = 0
      try {
        const ir = await api.get('yn_xm_item_list', { xmbh: p.xmbh })
        const items = ir.data || []
        if (items.length) {
          const names = []
          items.forEach(it => {
            const n = String(it.item_names || '')
            n.split(/[;；、,，]/).forEach(part => {
              const t = part.trim()
              if (t && !names.includes(t)) names.push(t)
            })
            if (it.basic_type && it.basic_type.includes('土壤')) xcssfa = '土壤'
            else if (it.basic_type && it.basic_type.includes('气')) xcssfa = '气体'
            else if (it.basic_type && it.basic_type.includes('水')) xcssfa = '水'
            xcssfaNum += parseInt(it.place_count || 0) || 1
          })
          jcxm = names.join('、')
        }
      } catch (e) { /* ignore */ }
      const sj = {
        rwbh: '', xmbh: p.xmbh, xmmc: p.xmmc, wtdw: p.wtdw_mc, xmfzr: p.xmfzr_xm || '',
        jcxm, xcssfa, xcssfa_num: xcssfaNum || undefined
      }
      const u = this.currentUser
      const res = await api.post('yn_bdsl_save', {
        mbid: this.mbid, bdmc: '任务派遣单',
        xmid: p.id, xmbh: p.xmbh, xmmc: p.xmmc,
        slbh: '',
        cjr_id: u.id,
        cjr_xm: u.xm,
        sj_json: JSON.stringify(sj), bdzt: 'draft'
      })
      this.showSelectProject = false
      const id = String(res.data).trim()
      if (!id || id === '11') { this.$message.error('保存失败'); return }
      this.editId = id
      this.saveMbId = this.mbid; this.saveXmid = p.id; this.saveXmmc = p.xmmc; this.saveBdmc = '任务派遣单'
      this.formData = { rwbh: '', xmbh: p.xmbh, xmmc: p.xmmc, wtdw: p.wtdw_mc, xmfzr: p.xmfzr_xm || '', jcxm, xcssfa, xcssfa_num: xcssfaNum || 1 }
      this.formReadonly = false
      this.formDialogTitle = '填写任务派遣单'
      this.showFill = true
    },
    async openTaskForm(row, { readonly = false } = {}) {
      this.loadingForm = true
      this.formData = null
      this.showFill = true
      try {
        const res = await api.get('yn_bdsl_get', { id: row.id })
        if (!res.data || !res.data.id) {
          this.$message.error('未找到记录')
          this.showFill = false
          return
        }
        this.editId = res.data.id
        this.saveRow = res.data
        this.saveMbId = res.data.mbid
        this.saveXmid = res.data.xmid
        this.saveXmmc = res.data.xmmc
        this.saveBdmc = res.data.bdmc
        try { this.formData = JSON.parse(res.data.sj_json || '{}') } catch (e) { this.formData = {} }
        this.formReadonly = readonly
        this.formDialogTitle = readonly ? '查看任务派遣单' : '填写任务派遣单'
      } catch (e) {
        this.$message.error('加载失败: ' + (e.message || e))
        this.showFill = false
      } finally {
        this.loadingForm = false
      }
    },
    fillForm(row) {
      if (!this.canEdit(row)) { this.$message.warning('无权编辑'); return }
      if (this.isRoutineDispatch(row)) {
        this.goRoutinePlan(row)
        return
      }
      this.openTaskForm(row, { readonly: false })
    },
    viewTask(row) {
      if (this.isRoutineDispatch(row)) {
        this.goRoutinePlan(row)
        return
      }
      this.openTaskForm(row, { readonly: !this.canEdit(row) })
    },
    onCyryChange(val) {
      if (val && !this.formData.ypjjry) {
        this.$set(this.formData, 'ypjjry', val)
      }
    },
    async saveForm() {
      if (!this.editId) { this.$message.error('请先通过新建任务创建记录'); return }
      const fd = this.formData
      if (fd.cyry && !fd.ypjjry) fd.ypjjry = fd.cyry
      const r = this.saveRow || {}
      const p = (this.projects || []).find(x => String(x.id) === String(this.saveXmid))
      const xmbh = fd.xmbh || (p && p.xmbh) || r.xmbh || ''
      if (p && p.xmbh) fd.xmbh = p.xmbh
      await api.post('yn_bdsl_save', {
        id: this.editId,
        mbid: this.saveMbId, bdmc: this.saveBdmc,
        xmid: this.saveXmid, xmmc: this.saveXmmc,
        xmbh,
        rwbh: fd.rwbh || '',
        cjr_id: r.cjr_id || this.currentUser.id,
        cjr_xm: r.cjr_xm || this.currentUser.xm,
        sj_json: JSON.stringify(fd)
      })
      this.showFill = false
      this.$message.success('保存成功，项目阶段已更新为「已派遣」')
      this.load()
    },
    async loadPhasesForList() {
      const xmbhs = [...new Set(this.list.map(r => this.rowXmbh(r)).filter(Boolean))]
      const map = {}
      await Promise.all(xmbhs.map(async xmbh => {
        try {
          const res = await api.get('yn_tr_project_phase', { xmbh })
          if (res.data && !res.data.error) map[xmbh] = res.data
        } catch (e) { /* ignore */ }
      }))
      this.phaseMap = map
    },
    rowXmbh(row) {
      return (row && row.xmbh) || this.sj(row).xmbh || ''
    },
    phaseForRow(row) {
      const xmbh = this.rowXmbh(row)
      return xmbh ? this.phaseMap[xmbh] : null
    },
    phaseTag(xmzt) {
      const m = { new: 'info', dispatched: '', sampling: 'warning', handover: 'warning', lab_ready: 'success', labing: 'primary', lab_done: 'success', done: 'success' }
      return m[xmzt] || 'info'
    },
    async del(row) {
      if (!this.canEdit(row)) { this.$message.warning('无权删除'); return }
      await this.$confirm(
        '删除派遣单将同时清除该项目的采样数据、054/056 表单及签字记录（理化录入保留）。确认删除？',
        '提示',
        { type: 'warning' }
      )
      await api.get('yn_bdsl_del', { id: row.id })
      this.$message.success('已删除，现场流程数据已重置')
      this.load()
    },
    ztTag(v) { return { draft: 'info', pending: 'warning', approved: 'success', rejected: 'danger' }[v] || 'info' },
    ztLabel(v) { return { draft: '草稿', pending: '待审核', approved: '已通过', rejected: '已驳回' }[v] || v },
  }
}
</script>

<style scoped>
.field-hint { font-size: 12px; color: #909399; line-height: 1.4; margin-top: 4px; }
.page-header { display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap; }
.header-actions { display: flex; align-items: center; gap: 8px; }
.filter-banner { margin-bottom: 12px; }
</style>
